import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Button from '@mui/material/Button';
import Popover from '@mui/material/Popover';
import Tooltip from '@mui/material/Tooltip';
import { useQuery } from '@tanstack/react-query';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmailVerificationModal } from 'components/EmailVerification/EmailVerificationModal';
import { currentUserVerificationQueries } from 'hooks/currentUserVerificationQueries';
import { ShareCardV3 } from './ShareCardV3';
import { DocumentShareDialog } from './DocumentShareDialog';
import { EmailSuccessModal } from './Email/EmailSuccessModal';

interface ShareDocumentButtonPopoverProps {
  contentId: string;
  previewURL: string;
  documentTitleWithCompanyName: string;
  canShare: boolean;
}

type ModalState = 'closed' | 'popper' | 'dialog' | 'emailSuccess' | 'verifying';

export const ShareDocumentButtonPopover: React.FC<ShareDocumentButtonPopoverProps> = ({
  contentId,
  previewURL,
  documentTitleWithCompanyName,
  canShare,
}) => {
  const [modalState, setModalState] = useState<ModalState>('closed');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const popperRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const { data: verification, refetch: refetchVerification } = useQuery({
    ...currentUserVerificationQueries.currentUser,
    enabled: modalState === 'popper' || modalState === 'verifying',
  });

  const handlePopper = (event?: React.MouseEvent<HTMLElement>) => {
    if (anchorEl || !event) {
      setAnchorEl(null);
      setModalState('closed');
    } else {
      setAnchorEl(event.currentTarget);
      setModalState('popper');
    }
  };

  const handleCloseModal = () => {
    setAnchorEl(null);
    setModalState('closed');
  };

  const handleOpenEmailShareDialog = async () => {
    setAnchorEl(null);
    const { data: latestVerification } = await refetchVerification();
    setModalState(latestVerification?.emailVerified === false ? 'verifying' : 'dialog');
  };

  useEffect(() => {
    if (modalState === 'verifying' && verification?.emailVerified === true) {
      setModalState('dialog');
    }
  }, [modalState, verification?.emailVerified]);

  return (
    <>
      <Tooltip title={!canShare ? t('share_document_v3.no_permission_tooltip') : ''} placement="bottom">
        <span>
          <Button
            variant="contained"
            color="primary"
            className="send-button"
            onClick={handlePopper}
            disabled={!canShare}
            endIcon={modalState === 'popper' ? <ExpandLess /> : <ExpandMore />}
          >
            {t('share_document')}
          </Button>
        </span>
      </Tooltip>
      <Popover
        disableScrollLock={true}
        data-testid="share-document-menu"
        open={modalState === 'popper' && !!anchorEl}
        anchorEl={anchorEl}
        sx={{
          '& .MuiPaper-root': {
            borderRadius: 4,
          },
        }}
        onClose={handleCloseModal}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <div ref={popperRef}>
          <ShareCardV3
            contentId={contentId}
            previewURL={previewURL}
            documentTitleWithCompanyName={documentTitleWithCompanyName}
            handleOpenEmailShareDialog={handleOpenEmailShareDialog}
          />
        </div>
      </Popover>
      <DocumentShareDialog
        contentId={contentId}
        documentTitleWithCompanyName={documentTitleWithCompanyName}
        isOpen={modalState === 'dialog'}
        onResolve={handleCloseModal}
        onSuccess={() => setModalState('emailSuccess')}
      />
      {verification && (
        <EmailVerificationModal
          open={modalState === 'verifying'}
          onClose={handleCloseModal}
          userId={verification.userId}
          email={verification.email}
          pendingEmail={verification.pendingEmail}
        />
      )}
      <EmailSuccessModal isOpen={modalState === 'emailSuccess'} onResolve={handleCloseModal} />
    </>
  );
};
